import { useMemo } from "react"
import { LineChart } from "@mui/x-charts"

import type { LinePlotView, RecordType } from "types/dashboard.types"
import type { FieldQualityMap } from "types/quality.types"

import { useDashboardContext } from "helpers/hooks/useDashboardContext"
import useMuiLineConfig from "widgets/plots/configuration/useMuiLineConfig"
import { getStyles } from "./styles"
import MuiReferenceLines from "./MuiReferenceLines"
import MuiSteppedReferenceLine from "./MuiSteppedReferenceLine"
import MuiLineTooltip from "./MuiLineTooltip"
import MuiChartZoom from "./zoom/MuiChartZoom"
import BackgroundRectangle from "./BackgroundRectangle"
import CrosshairLine from "./CrosshairLine"
import ChartHoverBridge from "./ChartHoverBridge"
import TooltipPositionBridge from "./TooltipPositionBridge"

interface MuiLineChartProps {
  view: LinePlotView
  data: RecordType[]
  tooltipData: RecordType[]
  fromDate?: number
  toDate?: number
  fieldQualityMap?: FieldQualityMap
}

const MuiLineChart = ({
  view,
  data,
  tooltipData,
  fromDate,
  toDate,
  fieldQualityMap,
}: MuiLineChartProps) => {
  const { entityType: contextEntity } = useDashboardContext()
  const config = useMuiLineConfig({
    view,
    data,
    tooltipData,
    fromDate,
    toDate,
    fieldQualityMap,
  })
  const {
    series,
    formattedData,
    tooltipDataByMillis,
    xAxis,
    yAxis,
    margin,
    xReferenceLines,
    yReferenceLines,
    yReferenceStepLine,
    legend,
    mark,
    tooltip,
    tooltipFormatter,
    grid,
    xDomainMin,
    xDomainMax,
    forecastStartDate,
    forecastEndDate,
  } = config
  const lineSx = useMemo(() => getStyles(config), [config])

  const slotProps = useMemo(
    () => ({
      tooltip: {
        trigger: tooltip?.trigger,
        tooltipData: tooltipDataByMillis,
        fieldQualityMap,
        tooltipFormatter,
      },
    }),
    [tooltipDataByMillis, tooltip?.trigger, fieldQualityMap, tooltipFormatter],
  )
  const slots = useMemo(
    () => ({ mark, tooltip: tooltip?.customComponent ?? MuiLineTooltip }),
    [mark, tooltip?.customComponent],
  )

  return (
    <>
      {series && formattedData && (
        <LineChart
          skipAnimation
          hideLegend={legend?.hidden}
          dataset={formattedData}
          margin={margin}
          xAxis={xAxis}
          yAxis={yAxis}
          series={series}
          grid={grid}
          sx={lineSx}
          slotProps={slotProps}
          slots={slots}
        >
          <TooltipPositionBridge />
          {forecastStartDate && forecastEndDate && (
            <BackgroundRectangle xStart={forecastStartDate} xEnd={forecastEndDate} />
          )}

          <MuiReferenceLines
            xReferenceLines={xReferenceLines}
            yReferenceLines={yReferenceLines}
          />
          {yReferenceStepLine && (
            <MuiSteppedReferenceLine steppedLine={yReferenceStepLine} />
          )}
          {contextEntity && (
            <CrosshairLine xDomainMin={xDomainMin} xDomainMax={xDomainMax} />
          )}
          {contextEntity && (
            <MuiChartZoom
              entity={contextEntity}
              xDomainMin={xDomainMin}
              xDomainMax={xDomainMax}
            />
          )}
          {contextEntity && (
            <ChartHoverBridge xDomainMin={xDomainMin} xDomainMax={xDomainMax} />
          )}
        </LineChart>
      )}
    </>
  )
}

export default MuiLineChart
